import { makeTheme } from 'dripsy';
import { Platform } from 'react-native';

function webFont(font: string) {
  return Platform.select({
    web: `${font}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, Inter-serif`,
    default: font,
  });
}

const neutrals = {
  // dark
  N900Slate: '#1B1919',
  N800SquidInk: '#242222',
  N700Snorlax: '#312E2E',
  N600PetRock: '#3C3838',
  N500McFanning: '#414141',
  // mid
  N400Concrete: '#505F79',
  N300Clooney: '#5E6C84',
  N200BlingBling: '#6B778C',
  N100Humboldt: '#7A869A',
  N90Meredith: '#8993A4',
  N80SpookyGhost: '#97A0AF',
  N70Blanche: '#A5ADBA',
  N60Sentinel: '#B3BAC5',
  // light
  N50Karl: '#C1C7D0',
  N40JollyFunTime: '#DFE1E6',
  N30Snow: '#EBECF0',
  N20GramsHair: '#F4F5F7',
  N10WashMe: '#FAFBFC',
  N0Doctor: '#FFFFFF',
};

const red = {
  R400: '#FF4E00',
  R300: '#FF5218',
  R200: '#FF632F',
  R100: '#FF855D',
  R90: '#FFAE8A',
  R80: '#FFDCD0',
};

const yellow = {
  Y400: '#FCC11D',
  Y300: '#FECB3E',
  Y200: '#FED564',
  Y100: '#FEDF8B',
  Y90: '#FEEAB1',
  Y80: '#FFFED8',
};

const green = {
  G400: '#C3EA44',
  G300: '#C4E851',
  G200: '#DBF587',
  G100: '#E4F7A5',
  G90: '#EDFAC3',
  G80: '#F6FCE1',
};

const blue = {
  B400: '#26D2FF',
  B300: '#57DCFF',
  B200: '#78E3FF',
  B100: '#9AEAFF',
  B90: '#BBF1FF',
  B80: '#DDF8FF',
};

const special = {
  shadow: '#000000',
  black: '#000000',
};

const font = 'LatoRegular';

export const theme = makeTheme({
  colors: {
    // Heading ()
    $heading900: neutrals.N900Slate,
    $heading800: neutrals.N800SquidInk,
    $heading700: neutrals.N700Snorlax,
    $heading600: neutrals.N600PetRock,
    $heading500: neutrals.N500McFanning,

    // Text
    $text400: neutrals.N400Concrete,
    $text300: neutrals.N300Clooney,
    $text200: neutrals.N200BlingBling,
    $text100: neutrals.N100Humboldt,
    $text90: neutrals.N90Meredith,
    $text80: neutrals.N80SpookyGhost,
    $text70: neutrals.N70Blanche,
    $text60: neutrals.N60Sentinel,

    // Background
    $background50: neutrals.N50Karl,
    $background40: neutrals.N40JollyFunTime,
    $background30: neutrals.N30Snow,
    $background20: neutrals.N20GramsHair,
    $background10: neutrals.N10WashMe,
    $background0: neutrals.N0Doctor,

    // Primary
    $primary400: green.G400,
    $primary300: green.G300,
    $primary200: green.G200,
    $primary100: green.G100,
    $primary90: green.G90,
    $primary80: green.G80,

    // Error
    $error400: red.R400,
    $error300: red.R300,
    $error200: red.R200,
    $error100: red.R100,
    $error90: red.R90,
    $error80: red.R80,

    // Info
    $info400: blue.B400,
    $info300: blue.B300,
    $info200: blue.B200,
    $info100: blue.B100,
    $info90: blue.B90,
    $info80: blue.B80,

    // Info
    $waring400: yellow.Y400,
    $waring300: yellow.Y300,
    $waring200: yellow.Y200,
    $waring100: yellow.Y100,
    $waring90: yellow.Y90,
    $waring80: yellow.Y80,

    // special
    $shadow: special.shadow,
    $black: special.black,
  },

  linearGradients: {
    videoPlayerControl: ['rgba(0,0,0,0.0)', 'rgba(0,0,0,0.2)'],
  },

  customFonts: {
    [font]: {
      default: webFont('LatoRegular'),
      normal: webFont('LatoRegular'),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '600': webFont('LatoSemiBold'),
    },
  },
  fonts: {
    root: font,
  },
  fontWeights: {
    semi: '600',
  },
  fontSizes: {
    xxsmall: 10,
    xsmall: 12,
    small: 14,
    medium: 16,
    large: 18,
    xlarge: 20,
    xxlarge: 22,
    xxxlarge: 24,
    big: 26,
    xbig: 28,
    xxbig: 30,
    xxxbig: 38,
    xxxxbig: 46,
    huge: 56,
  },
  lineHeights: {
    xxsmall: 14,
    xsmall: 16,
    small: 19,
    medium: 22,
    large: 24,
    xlarge: 27,
    xxlarge: 30,
    xxxlarge: 33,
    big: 35,
    xbig: 38,
    xxbig: 41,
    xxxbig: 52,
    xxxxbig: 63,
    huge: 76,
  },
  text: {
    xxsmall: {
      fontSize: 'xxsmall',
      lineHeight: 'xxsmall',
    },
    xsmall: {
      fontSize: 'xsmall',
      lineHeight: 'xsmall',
    },
    small: {
      fontSize: 'small',
      lineHeight: 'small',
    },
    medium: {
      fontSize: 'medium',
      lineHeight: 'medium',
    },
    large: {
      fontSize: 'large',
      lineHeight: 'large',
    },
    xlarge: {
      fontSize: 'xlarge',
      lineHeight: 'xlarge',
    },
    xxlarge: {
      fontSize: 'xxlarge',
      lineHeight: 'xxlarge',
    },
    xxxlarge: {
      fontSize: 'xxxlarge',
      lineHeight: 'xxxlarge',
    },
    big: {
      fontSize: 'big',
      lineHeight: 'big',
    },
    xbig: {
      fontSize: 'xbig',
      lineHeight: 'xbig',
    },
    xxbig: {
      fontSize: 'xxbig',
      lineHeight: 'xxbig',
    },
    xxxbig: {
      fontSize: 'xxxbig',
      lineHeight: 'xxxbig',
    },
    xxxxbig: {
      fontSize: 'xxxxbig',
      lineHeight: 'xxxxbig',
    },
    huge: {
      fontSize: 'huge',
      lineHeight: 'huge',
    },
  },

  space: {
    $0: 0,
    $1: 2,
    $2: 4,
    $3: 6,
    $4: 8,
    $5: 12,
    $6: 16,
    $7: 20,
    $8: 24,
    $9: 32,
    $10: 40,
    $12: 48,
    $13: 56,
    $14: 64,
    $15: 72,
    $16: 96,
  },

  borderRadius: {
    full: 9999,
  },

  shadows: {
    popover: {
      shadowRadius: 16,
      shadowColor: special.shadow,
      shadowOpacity: 0.1,
      shadowOffset: { height: 3, width: 3 },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/naming-convention
type MyTheme = typeof theme;
declare module 'dripsy' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface DripsyCustomTheme extends MyTheme {}
}
